import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PurchaseService } from '../../../services/purchaseService';
import { PurchaseReturn } from '../../../types/purchase';
import { formatCurrency } from '../../../utils/formatters';

export const PurchaseReturnsList: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [returns, setReturns] = useState<PurchaseReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReturn, setSelectedReturn] = useState<PurchaseReturn | null>(null);

  useEffect(() => {
    fetchReturns();
  }, [page, rowsPerPage, search]);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const response = await PurchaseService.getPurchaseReturns({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
      } as any);
      setReturns(response.purchase_returns || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch returns:', error);
      enqueueSnackbar('Failed to load purchase returns', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, returnItem: PurchaseReturn) => {
    setAnchorEl(event.currentTarget);
    setSelectedReturn(returnItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReturn(null);
  };

  const handleView = () => {
    if (selectedReturn) {
      navigate(`/admin/purchase/returns/${selectedReturn.id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedReturn) {
      navigate(`/admin/purchase/returns/${selectedReturn.id}/edit`);
    }
    handleMenuClose();
  };

  const handleSend = async () => {
    if (!selectedReturn) return;
    try {
      await PurchaseService.sendPurchaseReturn(selectedReturn.id);
      enqueueSnackbar('Return sent successfully', { variant: 'success' });
      fetchReturns();
    } catch (error) {
      enqueueSnackbar('Failed to send return', { variant: 'error' });
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
      draft: 'default',
      sent: 'primary',
      acknowledged: 'primary',
      processed: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading && returns.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Purchase Returns</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/purchase/returns/create')}
        >
          Create Return
        </Button>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search returns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Return Number</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Bill Number</TableCell>
              <TableCell>Return Date</TableCell>
              <TableCell align="right">Return Amount</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {returns.map((returnItem) => (
              <TableRow key={returnItem.id} hover>
                <TableCell>{returnItem.returnNumber}</TableCell>
                <TableCell>{returnItem.supplier?.businessName || '-'}</TableCell>
                <TableCell>{returnItem.purchaseBill?.billNumber || '-'}</TableCell>
                <TableCell>{new Date(returnItem.returnDate).toLocaleDateString()}</TableCell>
                <TableCell align="right">{formatCurrency(returnItem.totalAmount)}</TableCell>
                <TableCell>{returnItem.returnReason || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={returnItem.status.toUpperCase().replace('_', ' ')}
                    color={getStatusColor(returnItem.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, returnItem)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleView}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {selectedReturn?.status === 'draft' && (
          <>
            <MenuItem onClick={handleEdit}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            <MenuItem onClick={handleSend}>
              <SendIcon fontSize="small" sx={{ mr: 1 }} />
              Send to Supplier
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};
